// План → Расписание: правила по дням недели. Сохранение перестраивает только будущие невыполненные дни.

import { useState } from 'react'

import { Badge, Button, ErrorText, Modal, PageSpinner, Select, useToast } from '@/components/ui'

import { planApi, usePlanMutation, useRules, type Rule, type RuleInput } from './api'
import { WEEKDAYS_FULL, cardioLabel, draftFrom, draftToItem, type PlanItemDraft } from './logic'
import { PlanItemFields } from './parts'

const toInput = (r: Rule): RuleInput => ({
  id: r.id,
  weekday: r.weekday,
  kind: r.kind,
  template_id: r.template_id,
  cardio: r.cardio,
  active: r.active,
})

type Editing = { rule: Rule | null; weekday: number }

function RuleModal({
  editing,
  saving,
  error,
  onClose,
  onSave,
}: {
  editing: Editing | null
  saving: boolean
  error: unknown
  onClose: () => void
  onSave: (rule: RuleInput) => void
}) {
  const rule = editing?.rule ?? null
  const [weekday, setWeekday] = useState(String(editing?.weekday ?? 0))
  const [active, setActive] = useState(rule?.active ?? true)
  const [draft, setDraft] = useState<PlanItemDraft>(
    draftFrom(rule?.kind ?? 'strength', rule?.template_id ?? null, rule?.cardio ?? null),
  )
  const [problem, setProblem] = useState<string | null>(null)
  const submit = () => {
    const item = draftToItem(draft)
    if (!item.ok) {
      setProblem(item.error)
      return
    }
    setProblem(null)
    onSave({
      id: rule?.id ?? null,
      weekday: Number(weekday),
      kind: draft.kind,
      template_id: item.template_id,
      cardio: item.cardio,
      active,
    })
  }
  return (
    <Modal
      open={editing !== null}
      onClose={onClose}
      title={rule ? 'Правило расписания' : 'Новое правило'}
      footer={
        <Button variant="primary" loading={saving} onClick={submit}>
          Сохранить
        </Button>
      }
    >
      <div className="space-y-3">
        <Select
          label="День недели"
          options={WEEKDAYS_FULL.map((d, i) => ({ value: String(i), label: d }))}
          value={weekday}
          onChange={(e) => setWeekday(e.target.value)}
        />
        <PlanItemFields value={draft} onChange={setDraft} />
        <label className="flex min-h-11 items-center gap-2 text-sm text-fg">
          <input type="checkbox" className="h-4 w-4" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Правило действует
        </label>
        <ErrorText error={error}>{problem}</ErrorText>
      </div>
    </Modal>
  )
}

export function RulesPage() {
  const rules = useRules()
  const toast = useToast()
  const [editing, setEditing] = useState<Editing | null>(null)
  const save = usePlanMutation(planApi.setRules)
  const current = rules.data ?? []

  const commit = (next: RuleInput[], message: string) =>
    save.mutate(next, {
      onSuccess: () => {
        setEditing(null)
        toast.show({ message, tone: 'accent' })
      },
    })
  const upsert = (rule: RuleInput) => {
    const others = current.filter((r) => r.id !== rule.id).map(toInput)
    commit([...others, rule], 'Расписание сохранено — план на 4 недели перестроен')
  }
  const remove = (rule: Rule) =>
    commit(current.filter((r) => r.id !== rule.id).map(toInput), 'Правило удалено — будущие дни убраны из плана')

  if (rules.isPending) return <PageSpinner />
  if (rules.error) return <ErrorText error={rules.error} />

  return (
    <div className="min-w-0 space-y-3">
      <p className="text-sm text-muted">
        Из правил строится план на 4 недели вперёд и продлевается сам каждую ночь. Правка меняет только будущие
        невыполненные дни: проведённые, пропущенные и перенесённые вручную остаются как есть.
      </p>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {WEEKDAYS_FULL.map((name, weekday) => {
          const items = current.filter((r) => r.weekday === weekday)
          return (
            <li key={name} className="flex flex-wrap items-start gap-2 px-3 py-2.5 sm:px-4">
              <span className="w-28 shrink-0 pt-2.5 text-sm font-medium text-fg lg:pt-1.5">{name}</span>
              <div className="min-w-0 flex-1 space-y-1">
                {items.length ? (
                  items.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing({ rule: r, weekday })}
                        className="min-h-11 min-w-0 truncate text-left text-sm text-fg hover:text-accent lg:min-h-8"
                      >
                        {r.kind === 'cardio' && r.cardio ? cardioLabel(r.cardio) : (r.template_name ?? 'Шаблон удалён')}
                      </button>
                      {!r.active ? <Badge>выключено</Badge> : null}
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} disabled={save.isPending}>
                        Убрать
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="pt-2.5 text-sm text-muted lg:pt-1.5">Отдых</p>
                )}
              </div>
              <Button size="sm" icon="plus" onClick={() => setEditing({ rule: null, weekday })}>
                Добавить
              </Button>
            </li>
          )
        })}
      </ul>
      <ErrorText error={editing ? null : save.error} />
      {editing ? (
        <RuleModal
          key={`${editing.rule?.id ?? 'new'}-${editing.weekday}`}
          editing={editing}
          saving={save.isPending}
          error={save.error}
          onClose={() => {
            save.reset()
            setEditing(null)
          }}
          onSave={upsert}
        />
      ) : null}
    </div>
  )
}
